import type {
  ArtPack,
  ArtPackManifest,
  AuthoredAnimClip,
  AuthoredDirectionalAnimTable,
  SpriteTemplate,
} from "../../art/types";
import { loadAuthoredDirectionalAnimSet } from "../../art/anim-set";
import affiliation from "../tables/art/packs/raid/affiliation.json";
import packJson from "../tables/art/packs/raid/pack.json";
import palette from "../tables/art/packs/raid/palette.json";
import raid_tank from "../tables/art/packs/raid/sprites/raid_tank.json";
import raid_dps from "../tables/art/packs/raid/sprites/raid_dps.json";
import raid_healer from "../tables/art/packs/raid/sprites/raid_healer.json";
import raid_boss from "../tables/art/packs/raid/sprites/raid_boss.json";
import raid_add from "../tables/art/packs/raid/sprites/raid_add.json";
import raid_brute from "../tables/art/packs/raid/sprites/raid_brute.json";
import raid_charger from "../tables/art/packs/raid/sprites/raid_charger.json";
import fx_raid_tank_bash from "../tables/art/packs/raid/sprites/fx_raid_tank_bash.json";
import fx_raid_dps_bolt from "../tables/art/packs/raid/sprites/fx_raid_dps_bolt.json";
import fx_raid_healer_pulse from "../tables/art/packs/raid/sprites/fx_raid_healer_pulse.json";
import fx_raid_boss_roar from "../tables/art/packs/raid/sprites/fx_raid_boss_roar.json";
import fx_raid_brute_slash from "../tables/art/packs/raid/sprites/fx_raid_brute_slash.json";
import fx_raid_charger_streak from "../tables/art/packs/raid/sprites/fx_raid_charger_streak.json";
import raid_tankAnim from "../tables/art/packs/raid/anims/raid_tank.json";
import raid_dpsAnim from "../tables/art/packs/raid/anims/raid_dps.json";
import raid_healerAnim from "../tables/art/packs/raid/anims/raid_healer.json";
import raid_bossAnim from "../tables/art/packs/raid/anims/raid_boss.json";
import raid_addAnim from "../tables/art/packs/raid/anims/raid_add.json";
import raid_bruteAnim from "../tables/art/packs/raid/anims/raid_brute.json";
import raid_chargerAnim from "../tables/art/packs/raid/anims/raid_charger.json";
import fx_raid_tank_bashAnim from "../tables/art/packs/raid/anims/fx_raid_tank_bash.json";
import fx_raid_dps_boltAnim from "../tables/art/packs/raid/anims/fx_raid_dps_bolt.json";
import fx_raid_healer_pulseAnim from "../tables/art/packs/raid/anims/fx_raid_healer_pulse.json";
import fx_raid_boss_roarAnim from "../tables/art/packs/raid/anims/fx_raid_boss_roar.json";
import fx_raid_brute_slashAnim from "../tables/art/packs/raid/anims/fx_raid_brute_slash.json";
import fx_raid_charger_streakAnim from "../tables/art/packs/raid/anims/fx_raid_charger_streak.json";

const FX_IDS = new Set([
  "fx_raid_tank_bash",
  "fx_raid_dps_bolt",
  "fx_raid_healer_pulse",
  "fx_raid_boss_roar",
  "fx_raid_brute_slash",
  "fx_raid_charger_streak",
]);

const COMBAT_ANIMS: Record<string, AuthoredDirectionalAnimTable> = {
  raid_tank: raid_tankAnim as AuthoredDirectionalAnimTable,
  raid_dps: raid_dpsAnim as AuthoredDirectionalAnimTable,
  raid_healer: raid_healerAnim as AuthoredDirectionalAnimTable,
  raid_boss: raid_bossAnim as AuthoredDirectionalAnimTable,
  raid_add: raid_addAnim as AuthoredDirectionalAnimTable,
  raid_brute: raid_bruteAnim as AuthoredDirectionalAnimTable,
  raid_charger: raid_chargerAnim as AuthoredDirectionalAnimTable,
};

const FX_ANIMS: Record<string, AuthoredDirectionalAnimTable> = {
  fx_raid_tank_bash: fx_raid_tank_bashAnim as AuthoredDirectionalAnimTable,
  fx_raid_dps_bolt: fx_raid_dps_boltAnim as AuthoredDirectionalAnimTable,
  fx_raid_healer_pulse: fx_raid_healer_pulseAnim as AuthoredDirectionalAnimTable,
  fx_raid_boss_roar: fx_raid_boss_roarAnim as AuthoredDirectionalAnimTable,
  fx_raid_brute_slash: fx_raid_brute_slashAnim as AuthoredDirectionalAnimTable,
  fx_raid_charger_streak: fx_raid_charger_streakAnim as AuthoredDirectionalAnimTable,
};

const spriteList = [
  raid_tank,
  raid_dps,
  raid_healer,
  raid_boss,
  raid_add,
  raid_brute,
  raid_charger,
  fx_raid_tank_bash,
  fx_raid_dps_bolt,
  fx_raid_healer_pulse,
  fx_raid_boss_roar,
  fx_raid_brute_slash,
  fx_raid_charger_streak,
] as SpriteTemplate[];

/** VFX tables may only author idle — pad states so pack load cannot brick hub boot. */
function ensureFullStates(
  table: AuthoredDirectionalAnimTable,
): AuthoredDirectionalAnimTable {
  const dirs = { ...table.dirs };
  for (const d of ["e", "n", "s"] as const) {
    const src = dirs[d];
    if (!src?.idle?.frames?.length) continue;
    const idle = src.idle;
    const stub: AuthoredAnimClip = {
      fps: 1,
      loop: true,
      frames: [idle.frames[0]!],
    };
    dirs[d] = {
      idle,
      walk: src.walk ?? stub,
      attack: src.attack ?? { ...idle, loop: false },
      death: src.death ?? stub,
    };
  }
  return { dirs };
}

/** Raid fantasy combat kits (tank/dps/healer/boss) + FX sprites. */
export function loadRaidArtPack(): ArtPack {
  const sprites: Record<string, SpriteTemplate> = {};
  const anims: ArtPack["anims"] = {};
  for (const s of spriteList) {
    sprites[s.id] = s;
    const raw = COMBAT_ANIMS[s.id] ?? FX_ANIMS[s.id];
    if (!raw) continue;
    const authored = FX_IDS.has(s.id) ? ensureFullStates(raw) : raw;
    anims[s.id] = loadAuthoredDirectionalAnimSet(s.id, authored);
    const idle0 = authored.dirs.e.idle.frames[0];
    if (idle0) sprites[s.id] = { id: s.id, size: s.size, rows: idle0 };
  }
  return {
    manifest: packJson as ArtPackManifest,
    palette: palette as ArtPack["palette"],
    affiliation: affiliation as ArtPack["affiliation"],
    sprites,
    anims,
  };
}
